import { useTranslation } from 'react-i18next';
import { LegalPageLayout } from './LegalPageLayout';
import { LegalCompanyBlock } from './LegalCompanyBlock';
import { EmailLink } from './EmailLink';
import { useState, type FormEvent, type CSSProperties } from 'react';

export function ContactPage() {
  const { t } = useTranslation();
  const [result, setResult] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '6px',
  };

  const fieldStyle: CSSProperties = {
    width: '100%',
    borderRadius: '12px',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    padding: '12px 14px',
    color: '#111827',
    fontSize: '17px',
    lineHeight: 1.3,
    boxSizing: 'border-box',
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setIsSubmitting(true);
    setResult(t('legal.contact.form.sending'));

    try {
      const formData = new FormData(form);
      formData.append('access_key', '959f8090-4387-4114-800c-edbb75dc4660');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        setResult(t('legal.contact.form.success'));
        form.reset();
      } else {
        setResult(t('legal.contact.form.error'));
      }
    } catch {
      setResult(t('legal.contact.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LegalPageLayout
      title={t('legal.contact.title')}
      subtitle={t('legal.contact.subtitle')}
      maxWidthClassName="max-w-6xl"
      wrapInCard={false}
    >
      <div className="space-y-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              {t('legal.contact.section1Title')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t('legal.contact.section1Body')}
            </p>
            <div className="mt-3 text-lg">
              <EmailLink className="text-teal-600 hover:text-teal-700 underline font-semibold" />
            </div>
            <div className="mt-4">
              <LegalCompanyBlock />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              {t('legal.contact.form.title')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {t('legal.contact.form.subtitle')}
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ display: 'none' }}
              />
              <div>
                <label htmlFor="contact-name" style={labelStyle}>
                  {t('legal.contact.form.nameLabel')}
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  required
                  style={fieldStyle}
                  placeholder={t('legal.contact.form.namePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="contact-email" style={labelStyle}>
                  {t('legal.contact.form.emailLabel')}
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  required
                  style={fieldStyle}
                  placeholder={t('legal.contact.form.emailPlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="contact-message" style={labelStyle}>
                  {t('legal.contact.form.messageLabel')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  style={{ ...fieldStyle, minHeight: '150px', resize: 'vertical' }}
                  placeholder={t('legal.contact.form.messagePlaceholder')}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                style={{
                  borderRadius: '12px',
                  backgroundColor: '#0d9488',
                  minHeight: '52px',
                  fontSize: '18px',
                  lineHeight: 1,
                  padding: '10px 24px',
                  border: '0',
                }}
              >
                {isSubmitting ? t('legal.contact.form.submitting') : t('legal.contact.form.submit')}
              </button>
              <p aria-live="polite" className="text-sm text-gray-600">
                {result}
              </p>
            </form>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            {t('legal.contact.section2Title')}
          </h2>
          <p className="text-gray-600 leading-relaxed">
            {t('legal.contact.section2Body')}
          </p>
        </div>
      </div>
    </LegalPageLayout>
  );
}
